<template>
  <div class="orphan-page">
    <div class="toolbar">
      <div class="title-block">
        <h1>Чистка одиноких записей</h1>
        <span class="hint">Записи без ссылок в <code>executors</code></span>
      </div>
      <button class="btn" type="button" :disabled="loading" @click="fetchData">Обновить</button>
    </div>

    <div class="filters">
      <label class="field">
        <span>Тип записи</span>
        <select class="select" v-model="currentModel" @change="fetchData">
          <option v-for="opt in MODEL_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </label>
      <div class="filter-spacer"></div>
      <div class="counter-block">
        <span class="counter" v-if="!loading && !hasError">
          Найдено: <strong>{{ totalCount }}</strong>
        </span>
      </div>
    </div>

    <my-loader v-if="loading && items.length === 0 && !hasError" />

    <div v-else-if="hasError" class="state-block error">
      <p>Не удалось загрузить записи.</p>
      <p class="error-detail">{{ errorMessage }}</p>
    </div>

    <div v-else class="table-wrap">
      <table v-if="items.length" class="orphan-table">
        <thead>
          <tr>
            <th v-for="col in currentColumns" :key="col.key">{{ col.label }}</th>
            <th class="actions-col">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td v-for="col in currentColumns" :key="col.key">{{ formatCell(item, col.key) }}</td>
            <td class="actions-col">
              <button
                class="btn danger small"
                type="button"
                :disabled="deletingId === item.id"
                @click="onDelete(item)"
              >{{ deletingId === item.id ? '…' : 'Удалить' }}</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="state-block">
        Одиноких записей этого типа нет.
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { createToaster } from '@meforma/vue-toaster';
import { listOrphans, deleteOrphan } from '@/backend/orphanApi';

const MODEL_OPTIONS = [
  { value: 'companies', label: 'Компании' },
  { value: 'drivers', label: 'Водители' },
  { value: 'managers', label: 'Менеджеры' },
  { value: 'cars', label: 'Авто и прицепы' },
];

const COLUMNS = {
  companies: [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Название' },
    { key: 'inn', label: 'ИНН' },
    { key: 'ogrn', label: 'ОГРН' },
    { key: 'director', label: 'Директор' },
  ],
  drivers: [
    { key: 'id', label: 'ID' },
    { key: 'full_name', label: 'ФИО' },
    { key: 'phone', label: 'Телефон' },
    { key: 'passport', label: 'Паспорт' },
    { key: 'license', label: 'Вод. удостоверение' },
  ],
  managers: [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Имя' },
    { key: 'phone', label: 'Телефон' },
    { key: 'email', label: 'Email' },
  ],
  cars: [
    { key: 'id', label: 'ID' },
    { key: 'number', label: 'Номер' },
    { key: 'vin', label: 'VIN' },
    { key: 'ownership_type', label: 'Тип собственности' },
    { key: 'is_trailer', label: 'Прицеп' },
  ],
};

const EMPTY_VALUE = '—';

function formatDriverName(item) {
  const parts = [item.last_name, item.first_name, item.surname].filter(Boolean);
  return parts.length ? parts.join(' ') : EMPTY_VALUE;
}

export default {
  name: 'OrphanCleanupPage',
  setup() {
    const toaster = createToaster();
    const currentModel = ref('companies');
    const items = ref([]);
    const totalCount = ref(0);
    const loading = ref(false);
    const hasError = ref(false);
    const errorMessage = ref('');
    const deletingId = ref(null);

    const currentColumns = computed(() => COLUMNS[currentModel.value] || []);

    async function fetchData() {
      loading.value = true;
      hasError.value = false;
      errorMessage.value = '';
      try {
        const data = await listOrphans(currentModel.value);
        items.value = Array.isArray(data?.items) ? data.items : [];
        totalCount.value = typeof data?.count === 'number' ? data.count : items.value.length;
      } catch (err) {
        hasError.value = true;
        errorMessage.value = err?.response?.data?.detail || err?.message || 'Неизвестная ошибка';
        items.value = [];
        totalCount.value = 0;
      } finally {
        loading.value = false;
      }
    }

    function formatCell(item, key) {
      if (key === 'full_name') return formatDriverName(item);
      const v = item[key];
      if (v === null || v === undefined || v === '') return EMPTY_VALUE;
      if (key === 'is_trailer') return v ? 'да' : 'нет';
      return v;
    }

    async function onDelete(item) {
      const label = describeItem(item);
      const ok = window.confirm(`Удалить запись: ${label} (id=${item.id})?\nДействие необратимо.`);
      if (!ok) return;

      deletingId.value = item.id;
      try {
        const result = await deleteOrphan(currentModel.value, item.id);
        if (result?.deleted === false) {
          toaster.error('Недостаточно прав для удаления');
          return;
        }
        toaster.success('Запись удалена');
        items.value = items.value.filter((r) => r.id !== item.id);
        totalCount.value = Math.max(0, totalCount.value - 1);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 404) {
          toaster.info('Запись уже не существует или используется');
          items.value = items.value.filter((r) => r.id !== item.id);
        } else if (status === 409) {
          toaster.error(detail || 'Запись уже используется — нельзя удалить');
        } else {
          toaster.error(detail || err?.message || 'Не удалось удалить запись');
        }
      } finally {
        deletingId.value = null;
      }
    }

    function describeItem(item) {
      switch (currentModel.value) {
        case 'companies': return item.name || item.inn || `#${item.id}`;
        case 'drivers': return formatDriverName(item);
        case 'managers': return item.name || item.phone || `#${item.id}`;
        case 'cars': return item.number || item.vin || `#${item.id}`;
        default: return `#${item.id}`;
      }
    }

    fetchData();

    return {
      MODEL_OPTIONS,
      currentModel,
      currentColumns,
      items,
      totalCount,
      loading,
      hasError,
      errorMessage,
      deletingId,
      fetchData,
      formatCell,
      onDelete,
    };
  },
};
</script>

<style scoped>
.orphan-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 16px;
  color: #333;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

h1 { margin: 0; font-size: 22px; line-height: 1.2; }
.hint { color: #667085; font-size: 12px; }
.hint code { background: #f3f4f6; padding: 1px 4px; border-radius: 3px; font-size: 11px; }

.filters {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px;
  background: #f7f8fa;
  border: 1px solid #edf0f3;
  border-radius: 6px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  color: #4b5563;
}

.select {
  height: 30px;
  min-width: 200px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  padding: 0 6px;
}

.filter-spacer { flex: 1; }
.counter-block { font-size: 13px; color: #4b5563; }
.counter strong { color: #c0392b; }

.btn {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}
.btn:hover { border-color: #007bff; color: #007bff; }
.btn.danger { background: #fff; color: #c0392b; border-color: #f0b6b0; }
.btn.danger:hover { background: #c0392b; color: #fff; border-color: #c0392b; }
.btn.danger.small { height: 26px; padding: 0 10px; font-size: 12px; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }

.table-wrap { flex: 1; overflow: auto; }

.orphan-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.orphan-table th,
.orphan-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

.orphan-table th {
  background: #f7f8fa;
  color: #444;
  font-weight: 700;
}

.actions-col { width: 110px; }

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
  text-align: center;
}
.state-block.error { color: #dc3545; }
.error-detail { font-size: 12px; margin-top: 6px; color: #98a2b3; }
</style>
